/**
 * GOVX Design Tokens — Single Source of Truth
 *
 * Extracted from Figma "GOVX Colors (C)" and "Admin Typography" libraries.
 * Both theme.js (MUI) and tailwind.config.js should import from here.
 *
 * @see design-system/figma-alignment.md for the full Figma ↔ Code comparison
 * @see https://www.figma.com/design/Pn4XjaPxIPQgYXGMf4SEBY/GOVX-Colors--C-
 */

// =============================================================================
// COLOR TOKENS
// =============================================================================

export const colors = {
  // ---------------------------------------------------------------------------
  // Standard Base (primary brand colors)
  // ---------------------------------------------------------------------------
  blue: {
    dark: '#0E5777',    // Standard Base/bg-blue — primary brand, buttons, links
    light: '#0C9AC4',   // Standard Base/blue-500 — accent blue, step icons
    50: '#E1F4FB',
    100: '#CFEAF8',
    200: '#B1E2F5',
    300: '#7ECFEA',
    400: '#4AB6DC',
    500: '#0C9AC4',     // same as blue.light
    600: '#0A85A8',
    700: '#086E8D',
    900: '#063E52',
  },

  gray: {
    dark: '#2A2D36',    // Standard Base/gray-900 — primary text
    medium: '#7B7E84',  // Standard Base/gray-600 — secondary text
    light: '#E3E3E3',   // Standard Base/gray-300 — borders
    subtle: '#F7F7F7',  // Standard Base/gray-50 — background
    50: '#F7F7F7',
    100: '#F4F4F4',
    200: '#EEEEEE',
    300: '#E3E3E3',     // same as gray.light
    400: '#E0E0E0',
    500: '#BFBFBF',
    600: '#7B7E84',     // same as gray.medium
    700: '#5A5D63',
    800: '#323440',
    900: '#2A2D36',     // same as gray.dark
  },

  grayBlue: {
    100: '#ECF0F1',
    300: '#9DBCCA',
    500: '#6999AE',
  },

  // ---------------------------------------------------------------------------
  // Standard Highlight (semantic/status colors)
  // ---------------------------------------------------------------------------
  red: {
    DEFAULT: '#BD2326', // Standard Highlight/bg-red — error states
    50: '#FDEAEA',
    100: '#FBD5D5',
    200: '#F8A9A9',
    300: '#F47E7E',
    400: '#F15353',
    500: '#ED2828',
    600: '#D52424',
    800: '#961C1E',
    900: '#701616',
  },

  orange: {
    DEFAULT: '#D74F27', // Standard Highlight/bg-orange — warnings, accents
  },

  yellow: {
    DEFAULT: '#F7B918', // Standard Highlight/bg-yellow — caution
    50: '#FFF8E5',
    100: '#FFEFCC',
    200: '#FFE099',
    300: '#FFD266',
    400: '#FFC333',
    500: '#FFB500',
    700: '#D29D15',
    800: '#AD8111',
    900: '#88650D',
  },

  gold: {
    DEFAULT: '#B79042', // Standard Highlight/bg-gold — premium/verified
    50: '#FAF4E9',
    100: '#F5E9D4',
    200: '#E7D0A9',
    300: '#D8B77F',
    400: '#CAA055',
    500: '#BC8830',
    700: '#987637',
    800: '#785C2B',
    900: '#58431F',
  },

  green: {
    DEFAULT: '#1F8623', // Standard Highlight/bg-green — success
    50: '#E5F5E6',
    100: '#CBEACD',
    200: '#97D59B',
    300: '#62C068',
    400: '#4BAF50',
    500: '#1F8623',     // same as green.DEFAULT
    600: '#1B7520',
    700: '#16641C',
    800: '#125319',
    900: '#0D4215',
  },

  storefront: {
    red: '#972E0F',     // Storefront/Storefront Red
  },

  // ---------------------------------------------------------------------------
  // Semantic aliases (for clarity in component code)
  // ---------------------------------------------------------------------------
  primary: '#0E5777',
  accent: '#0C9AC4',
  error: '#BD2326',
  warning: '#D74F27',
  success: '#1F8623',
  info: '#0C9AC4',
  text: {
    primary: '#2A2D36',
    secondary: '#7B7E84',
    disabled: 'rgba(0, 0, 0, 0.26)',
  },
  border: '#E3E3E3',
  background: {
    default: '#FFFFFF',
    subtle: '#F7F7F7',
    muted: '#F1F1F1',   // Table headers, filter panels (from Figma Tables/Filters)
  },
  status: {
    active: '#2ecc71',    // StatusIndicator green circle
    inactive: '#f44336',  // StatusIndicator red circle
  },
  sellerStatus: {
    listed: {
      background: '#E5F5EC',
      border: '#B8E0C8',
      foreground: '#1C7A4D',
    },
    off: {
      background: '#FBEBEA',
      border: '#F0C3C1',
      foreground: '#B83B3B',
    },
    neutral: {
      background: '#E8F0FA',
      border: '#BFD6EE',
      foreground: '#2A5C8F',
    },
    unavailable: '#7C9094',
  },
}

// =============================================================================
// TYPOGRAPHY TOKENS
// =============================================================================

export const typography = {
  fontFamily: {
    sans: '"Roboto Regular", Helvetica, sans-serif',
    // Roboto's medium cut is a separate family here, not font-weight: 500.
    sansMedium: '"Roboto Medium", Helvetica, sans-serif',
    bold: '"Roboto Bold", Helvetica, sans-serif',
  },

  fontWeight: {
    regular: 400,
    bold: 700,  // Figma specifies 700, not 600
  },

  fontSize: {
    xs: '0.75rem',    // 12px — Small Text, H4
    sm: '0.875rem',   // 14px — Medium Text, Paragraph, Bullets, H3
    base: '1rem',     // 16px — Large Text, H2
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px — H1
    '2xl': '1.5rem',  // 24px
  },

  lineHeight: {
    tight: '100%',    // Headings, Small/Medium/Large Text, Bullets
    relaxed: '125%',  // Paragraph
  },

  // Pre-composed heading styles matching Figma "Admin Typography"
  headings: {
    h1: { fontFamily: '"Roboto Bold", Helvetica, sans-serif', fontWeight: 700, fontSize: '1.25rem', lineHeight: '100%' },
    h2: { fontFamily: '"Roboto Bold", Helvetica, sans-serif', fontWeight: 700, fontSize: '1rem', lineHeight: '100%' },
    h3: { fontFamily: '"Roboto Bold", Helvetica, sans-serif', fontWeight: 700, fontSize: '0.875rem', lineHeight: '100%' },
    h4: { fontFamily: '"Roboto Bold", Helvetica, sans-serif', fontWeight: 700, fontSize: '0.75rem', lineHeight: '100%' },
  },
}

// =============================================================================
// SPACING TOKENS (from component analysis — not explicitly in Figma tokens file)
// =============================================================================

export const spacing = {
  buttonPadding: '8px 16px',
  buttonMinHeight: '36px',
  buttonMinWidth: '64px',
  buttonRadius: '4px',
  buttonLetterSpacing: '0.028px',
  inputPadding: '24px 12px 8px 12px',
  tabPadding: '0 36px',
  tabMinWidth: '160px',
  tableCellHeight: '55px',
  tableCellPadding: '18px 14px',
  tableHeaderPadding: '20px 32px 36px',
  filterPanelPadding: '20px',
  filterPanelRadius: '4px',
}

// =============================================================================
// ELEVATION TOKENS
// =============================================================================

export const elevation = {
  button: '0px 1px 5px 0px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 3px 1px -2px rgb(0 0 0 / 12%)',
  buttonActive: '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
}
